import { Table, TableBody } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { useState, useEffect } from "react";
import { AnalysisTableRow } from "./analyses/AnalysisTableRow";
import { AnalysisTableHeader } from "./analyses/AnalysisTableHeader";
import { AnalysisSearch } from "./analyses/AnalysisSearch";
import { AnalysisPagination } from "./analyses/AnalysisPagination";
import { normalizeText } from "@/utils/textNormalization";

interface RecentAnalysis {
  id: string;
  url: string;
  keyword: string;
  timestamp: number;
  seo_score?: number;
}

const ITEMS_PER_PAGE = 10;

export const RecentAnalyses = () => {
  const { toast } = useToast();
  const [analyses, setAnalyses] = useState<RecentAnalysis[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedAnalyses, setSelectedAnalyses] = useState<Set<string>>(new Set());

  useEffect(() => {
    const storedAnalyses = JSON.parse(localStorage.getItem('recentAnalyses') || '[]');
    setAnalyses(storedAnalyses);
  }, []);

  if (analyses.length === 0) {
    return null;
  }

  const handleDelete = (e: React.MouseEvent, analysis: RecentAnalysis) => {
    e.stopPropagation();
    
    const updatedAnalyses = analyses.filter(a => a.id !== analysis.id);
    setAnalyses(updatedAnalyses);
    localStorage.setItem('recentAnalyses', JSON.stringify(updatedAnalyses));
    setSelectedAnalyses(prev => {
      const newSet = new Set(prev);
      newSet.delete(analysis.id);
      return newSet;
    });

    toast({
      title: "Análise excluída",
      action: <ToastAction altText="Desfazer exclusão" onClick={() => {
        const updatedAnalysesWithUndo = [...analyses];
        setAnalyses(updatedAnalysesWithUndo);
        localStorage.setItem('recentAnalyses', JSON.stringify(updatedAnalysesWithUndo));
      }}>
        Desfazer
      </ToastAction>,
      duration: 5000,
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(currentAnalyses.map(analysis => analysis.id));
      setSelectedAnalyses(newSelected);
    } else {
      setSelectedAnalyses(new Set());
    }
  };

  const handleSelect = (analysisId: string, checked: boolean) => {
    setSelectedAnalyses(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(analysisId);
      } else {
        newSet.delete(analysisId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = () => {
    const updatedAnalyses = analyses.filter(a => !selectedAnalyses.has(a.id));
    const deletedAnalyses = analyses.filter(a => selectedAnalyses.has(a.id));
    
    setAnalyses(updatedAnalyses);
    localStorage.setItem('recentAnalyses', JSON.stringify(updatedAnalyses));
    setSelectedAnalyses(new Set());

    toast({
      title: `${deletedAnalyses.length} análises excluídas`,
      action: <ToastAction altText="Desfazer exclusão" onClick={() => {
        const updatedAnalysesWithUndo = [...analyses];
        setAnalyses(updatedAnalysesWithUndo);
        localStorage.setItem('recentAnalyses', JSON.stringify(updatedAnalysesWithUndo));
      }}>
        Desfazer
      </ToastAction>,
      duration: 5000,
    });
  };

  const filteredAnalyses = analyses.filter(analysis => {
    const normalizedSearchTerm = normalizeText(searchTerm);
    const normalizedUrl = normalizeText(analysis.url);
    const normalizedKeyword = normalizeText(analysis.keyword);

    return (
      normalizedUrl.includes(normalizedSearchTerm) ||
      normalizedKeyword.includes(normalizedSearchTerm)
    );
  });

  const totalPages = Math.ceil(filteredAnalyses.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentAnalyses = filteredAnalyses
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(startIndex, endIndex);

  return (
    <div className="mt-16">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Análises recentes</h2>
        {selectedAnalyses.size > 0 && (
          <div className="flex gap-2">
            <Button
              variant="destructive"
              size="sm"
              onClick={handleBulkDelete}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Excluir selecionadas
            </Button>
          </div>
        )}
      </div>
      
      <AnalysisSearch 
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />

      <div className="overflow-x-auto">
        <Table>
          <AnalysisTableHeader 
            onSelectAll={handleSelectAll}
            allSelected={selectedAnalyses.size === currentAnalyses.length}
            someSelected={selectedAnalyses.size > 0 && selectedAnalyses.size < currentAnalyses.length}
          />
          <TableBody>
            {currentAnalyses.map((analysis) => (
              <AnalysisTableRow
                key={analysis.id}
                analysis={analysis}
                onDelete={handleDelete}
                selected={selectedAnalyses.has(analysis.id)}
                onSelect={handleSelect}
              />
            ))}
          </TableBody>
        </Table>

        <AnalysisPagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};